import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  Activity,
  LayoutDashboard,
  Monitor,
  AlertTriangle,
  Settings,
  TrendingUp,
  FileText,
  Cog,
  TestTube,
  Users,
  History,
  Bell,
  Lightbulb,
  ChevronDown,
  Search,
  Server,
  Brain
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { getDevices, getAlerts, getNotifications } from "../api/axios";
import { useAuth } from "../context/AuthContext";

type NavDropdownProps = {
  label: string;
  items: Array<{
    href: string;
    icon: React.ReactNode;
    label: string;
  }>;
};

function NavDropdown({ label, items }: NavDropdownProps) {
  const location = useLocation();
  const hasActiveItem = items.some(item => location.pathname === item.href);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-10 px-4 text-sm font-medium transition-colors",
            hasActiveItem
              ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          )}
        >
          {label}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {items.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <DropdownMenuItem key={item.href} asChild>
              <Link
                to={item.href}
                className={cn(
                  "flex items-center w-full cursor-pointer",
                  isActive && "bg-blue-50 text-blue-700"
                )}
              >
                <span className="mr-3 flex-shrink-0">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function TopNav() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout, user: profile, loading: profileLoading, refreshSession } = useAuth();

  // Fetch data for search
  const { data: devices = [], isLoading: isLoadingDevices } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: alerts = [], isLoading: isLoadingAlerts } = useQuery({
    queryKey: ["/api/alerts"],
    queryFn: () => getAlerts({ resolved: false }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  const { data: notifications = [] } = useQuery({
    queryKey: ["/api/notifications"],
    queryFn: getNotifications,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  // Count unresolved alerts (for live alerts badge)
  const unreadAlertCount = alerts.filter((alert: any) => !alert.acknowledged && !alert.resolved).length;
  // Count unread notifications (for notification bell badge)
  const unreadNotificationCount = notifications.filter((n: any) => !n.read).length;

  // Filter search results
  const searchResults = {
    devices: devices.filter((device: any) =>
      device.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.ipAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.type?.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 5),
    alerts: alerts.filter((alert: any) =>
      alert.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.details?.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 3),
  };

  const allResults = [
    ...searchResults.devices.map((device: any) => ({ 
      type: 'device', 
      data: device,
      label: device.name,
      subtitle: device.ipAddress,
      path: `/device/${device._id || device.id}`
    })),
    ...searchResults.alerts.map((alert: any) => ({ 
      type: 'alert', 
      data: alert,
      label: alert.details || alert.message,
      subtitle: `${alert.severity} alert`,
      path: '/alerts'
    }))
  ];

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSearchResults(value.length > 0);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSearchResults || allResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && allResults[selectedIndex]) {
          navigate(allResults[selectedIndex].path);
          setSearchTerm('');
          setShowSearchResults(false);
        }
        break;
      case 'Escape':
        setShowSearchResults(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const monitoringItems = [
    {
      href: "/dashboard",
      icon: <LayoutDashboard className="h-4 w-4" />,
      label: "Dashboard",
    },
    {
      href: "/manage-devices",
      icon: <Monitor className="h-4 w-4" />,
      label: "Device Inventory",
    },
    {
      href: "/live-alerts",
      icon: <AlertTriangle className="h-4 w-4" />,
      label: "Live Alerts",
    },
    {
      href: "/performance",
      icon: <TrendingUp className="h-4 w-4" />,
      label: "Performance",
    },
    // {
    //   href: "/network-topology",
    //   icon: <Network className="h-4 w-4" />,
    //   label: "Network Topology",
    // },
    {
      href: "/reports",
      icon: <FileText className="h-4 w-4" />,
      label: "Reports",
    },
  ];

  const aiAnalysisItems = [
    {
      href: "/ai-dashboard",
      icon: <LayoutDashboard className="h-4 w-4" />,
      label: "AI Dashboard",
    },
    {
      href: "/ai-insights",
      icon: <Lightbulb className="h-4 w-4" />,
      label: "AI Insights",
    },
    {
      href: "/anomaly-detection",
      icon: <AlertTriangle className="h-4 w-4" />,
      label: "Anomaly Detection",
    },
    {
      href: "/predictive-analytics",
      icon: <TrendingUp className="h-4 w-4" />,
      label: "Predictive Analytics",
    },
    {
      href: "/ai-reports",
      icon: <FileText className="h-4 w-4" />,
      label: "AI Reports",
    },
  ];

  const configurationItems = [
    {
      href: "/device-management",
      icon: <Cog className="h-4 w-4" />,
      label: "Device Management",
    },
    {
      href: "/ai-configuration",
      icon: <Brain className="h-4 w-4" />,
      label: "AI Configuration",
    },
    // {
    //   href: "/sla-monitoring",
    //   icon: <ShieldCheck className="h-4 w-4" />,
    //   label: "SLA Monitoring",
    // },
    {
      href: "/alerts",
      icon: <Bell className="h-4 w-4" />,
      label: "Alert Management",
    },
    {
      href: "/test-utilities",
      icon: <TestTube className="h-4 w-4" />,
      label: "Test Utilities",
    },
  ];

  const administrationItems = [
    {
      href: "/settings",
      icon: <Settings className="h-4 w-4" />,
      label: "Settings",
    },
    {
      href: "/user-management",
      icon: <Users className="h-4 w-4" />,
      label: "User Management",
    },
    {
      href: "/audit-logs",
      icon: <History className="h-4 w-4" />,
      label: "Audit Logs",
    },
    {
      href: "/notifications",
      icon: <Bell className="h-4 w-4" />,
      label: "Notifications",
    },
  ];

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0 z-50">
      <div className="flex items-center justify-between h-16 px-4 md:px-6 gap-4">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center space-x-3 flex-shrink-0">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2 rounded-xl shadow-md">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div className="hidden sm:flex flex-col">
            <span className="text-lg font-bold text-gray-900">NetMonitor</span>
            <span className="text-xs text-gray-500 font-medium">Network Management</span>
          </div>
        </Link>

        {/* Search Bar */}
        <div className="flex-1 max-w-2xl relative" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              className="pl-10 bg-gray-50 border-gray-200 focus:bg-white w-full"
              placeholder="Search devices, IP addresses, alerts..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => searchTerm && setShowSearchResults(true)}
            />
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchTerm && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
                {(isLoadingDevices || isLoadingAlerts) ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="animate-spin inline-block w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full mr-2"></div>
                    Searching...
                  </div>
                ) : allResults.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No results found for "{searchTerm}"
                  </div>
                ) : (
                  <div className="py-2">
                    {searchResults.devices.length > 0 && (
                      <>
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Devices ({searchResults.devices.length})
                        </div>
                        {searchResults.devices.map((device: any, index: number) => {
                          const globalIndex = index;
                          return (
                            <button
                              key={device._id || device.id}
                              className={cn(
                                "w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-3",
                                selectedIndex === globalIndex && 'bg-blue-50 border-r-2 border-blue-500'
                              )}
                              onClick={() => {
                                navigate(`/device/${device._id || device.id}`);
                                setSearchTerm('');
                                setShowSearchResults(false);
                              }}
                            >
                              <Server className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {device.name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {device.ipAddress} • {device.type}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}
                    
                    {searchResults.alerts.length > 0 && (
                      <>
                        {searchResults.devices.length > 0 && <div className="border-t border-gray-100 my-2" />}
                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Alerts ({searchResults.alerts.length})
                        </div>
                        {searchResults.alerts.map((alert: any, index: number) => {
                          const globalIndex = searchResults.devices.length + index;
                          return (
                            <button
                              key={alert._id || alert.id}
                              className={cn(
                                "w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-3",
                                selectedIndex === globalIndex && 'bg-blue-50 border-r-2 border-blue-500'
                              )}
                              onClick={() => {
                                navigate('/alerts');
                                setSearchTerm('');
                                setShowSearchResults(false);
                              }}
                            >
                              <AlertTriangle className={cn(
                                "h-4 w-4 flex-shrink-0",
                                alert.severity === 'critical' ? 'text-red-500' :
                                alert.severity === 'warning' ? 'text-yellow-500' : 'text-blue-500'
                              )} />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {alert.details || alert.message}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {alert.severity} alert
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}
                    
                    {searchTerm.length >= 2 && (
                      <>
                        <div className="border-t border-gray-100 my-2" />
                        <button
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 text-blue-600"
                          onClick={() => {
                            navigate(`/manage-devices?search=${encodeURIComponent(searchTerm)}`);
                            setSearchTerm('');
                            setShowSearchResults(false);
                          }}
                        >
                          <Search className="h-4 w-4" />
                          <span className="text-sm">Search all devices for "{searchTerm}"</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex items-center space-x-1 overflow-x-auto flex-shrink-0">
          <NavDropdown label="Monitoring" items={monitoringItems} />
          <NavDropdown label="AI Analysis" items={aiAnalysisItems} />
          {(profile?.role === 'admin' || profile?.role === 'operator') && <NavDropdown label="Configuration" items={configurationItems} />}
          {profile?.role === 'admin' && <NavDropdown label="Administration" items={administrationItems} />}
        </div>

        {/* Right Actions - Notification, Live Alerts, Profile */}
        <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0 ml-4">
          {/* Notifications (bell → /notifications, backend-connected) */}
          <Button
            variant="ghost"
            size="sm"
            className="relative p-2"
            asChild
          >
            <Link to="/notifications" title="Notifications">
              <Bell className="h-5 w-5 text-gray-600" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-amber-500 rounded-full min-w-[20px]">
                  {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                </span>
              )}
            </Link>
          </Button>
          {/* Live Alerts (exclamation/AlertTriangle → /live-alerts, backend alerts) */}
          <Button
            variant="ghost"
            size="sm"
            className="relative p-2"
            asChild
          >
            <Link to="/live-alerts" title="Live Alerts">
              <AlertTriangle className="h-5 w-5 text-gray-600" />
              {unreadAlertCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[20px]">
                  {unreadAlertCount > 99 ? '99+' : unreadAlertCount}
                </span>
              )}
            </Link>
          </Button>

          {/* User Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center space-x-2 p-1 h-auto"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                    alt="User avatar"
                  />
                  <AvatarFallback className="bg-blue-100 text-blue-600">
                    {profile?.displayname?.[0]?.toUpperCase() || profile?.username?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left hidden sm:block">
                  {profileLoading ? (
                    <>
                      <div className="h-4 bg-gray-200 rounded animate-pulse mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-16"></div>
                    </>
                  ) : profile ? (
                    <>
                      <p className="text-sm font-medium text-gray-900">{profile.displayname || profile.username || 'Unknown User'}</p>
                      <p className="text-xs text-gray-500">{profile.role || "User"}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900">Profile Error</p>
                      <button 
                        onClick={refreshSession} 
                        className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer"
                      >
                        Retry
                      </button>
                    </>
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile">Profile Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings">Application Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
